/**
 * Race Registration Report Page
 *
 * Admin report showing per-event race registration status for all active student riders.
 * - Season selector defaulting to current season; re-fetches events on change
 * - Fetches riders and events, filters events to Race type, filters riders to active Student/Rider types
 * - Summary with total rider count and Race events list
 * - Table: Name, Category, Group, Grade, then one column per Race event (sorted by event_starttime)
 * - Green check / red X icons for registration status
 * - Riders sorted alphabetically by name
 * - Loading indicator, error message, empty state
 * - Back navigation to Reports Hub, page title "Race Registration Report"
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 10.1, 10.2
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient, Event } from '../../api/rest-client';
import { Button } from '../../components/common/Button';
import {
  filterRaceEvents,
  sortRaceEventsByStartTime,
  filterActiveStudentRiders,
  transformRiderReportRow,
  sortRidersByName,
  getRegistrationStatus,
  applyReportFilters,
  countRegistrationsPerRace,
  countRacesForRider,
  countRidersRegisteredForAnyRace,
  countTotalRegistrations,
  generateRaceRegistrationCSV,
  generateRaceRegistrationCSVFilename,
  type ReportRow,
} from '../../utils/raceRegistration';

function getCurrentSeason(): string {
  return new Date().getFullYear().toString();
}

const RaceRegistrationReport: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [riders, setRiders] = useState<ReportRow[]>([]);
  const [raceEvents, setRaceEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Season state
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason());
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);

  // Filter state
  const [groupFilter, setGroupFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Derive unique groups from riders for the group dropdown
  const uniqueGroups = useMemo(() => {
    const groups = riders
      .map((r) => r.group)
      .filter((g) => g !== '');
    return [...new Set(groups)].sort((a, b) => a.localeCompare(b));
  }, [riders]);

  // Derive unique categories from riders for the category dropdown
  const uniqueCategories = useMemo(() => {
    const categories = riders
      .map((r) => r.category)
      .filter((c) => c !== '');
    return [...new Set(categories)].sort((a, b) => a.localeCompare(b));
  }, [riders]);

  // Derive unique grades from riders for the grade dropdown
  const uniqueGrades = useMemo(() => {
    const grades = riders
      .map((r) => r.grade)
      .filter((g) => g !== '');
    return [...new Set(grades)].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [riders]);

  // Apply filters to riders
  const filteredRiders = useMemo(
    () => applyReportFilters(riders, groupFilter, searchTerm, categoryFilter, gradeFilter),
    [riders, groupFilter, searchTerm, categoryFilter, gradeFilter],
  );

  // Per-race and aggregate counts over the currently shown riders (reflect active filters)
  const perRaceCounts = useMemo(
    () => countRegistrationsPerRace(filteredRiders, raceEvents),
    [filteredRiders, raceEvents],
  );
  const registeredAtLeastOne = useMemo(
    () => countRidersRegisteredForAnyRace(filteredRiders, raceEvents),
    [filteredRiders, raceEvents],
  );
  const totalRegistrations = useMemo(
    () => countTotalRegistrations(filteredRiders, raceEvents),
    [filteredRiders, raceEvents],
  );

  // Load season config on mount
  useEffect(() => {
    loadSeasonConfig();
  }, []);

  // Load data when season changes
  useEffect(() => {
    if (selectedSeason) {
      loadData(selectedSeason);
    }
  }, [selectedSeason]);

  const loadSeasonConfig = async () => {
    try {
      const config = await restClient.getSeasonConfig();
      setAvailableSeasons(config.availableSeasons);
      setSelectedSeason(config.currentSeason);
    } catch {
      // Fall back to current year if season config fails
      setAvailableSeasons([getCurrentSeason()]);
    }
  };

  const loadData = async (season: string) => {
    try {
      setLoading(true);
      setError(null);

      const [allRiders, allEvents] = await Promise.all([
        restClient.getRiders(),
        restClient.getEvents(season),
      ]);

      // Filter events to Race type and sort by start time
      const filtered = filterRaceEvents(allEvents);
      const sorted = sortRaceEventsByStartTime(filtered);
      setRaceEvents(sorted);

      // Filter riders to active Student/Rider types in the selected season
      const activeStudents = filterActiveStudentRiders(allRiders).filter(
        (r) =>
          r.season === season ||
          (Array.isArray(r.seasons_active) && r.seasons_active.includes(season)),
      );

      // Transform and sort riders
      const rows = activeStudents.map(transformRiderReportRow);
      setRiders(sortRidersByName(rows));
    } catch {
      setError('Failed to load race registration data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSeason(e.target.value);
  };

  const handleExportCSV = () => {
    const csv = generateRaceRegistrationCSV(filteredRiders, raceEvents);
    const filename = generateRaceRegistrationCSVFilename(new Date());
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center" role="status">
          Loading race registration data...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold">Race Registration Report</h1>
        <div className="flex gap-2">
          <Button variant="secondary" className="min-h-[44px]" onClick={handleExportCSV}>
            <svg
              className="w-4 h-4 mr-2 inline"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export CSV
          </Button>
          <Button variant="secondary" className="min-h-[44px]" onClick={() => navigate('/admin/reports')}>
            <svg
              className="w-4 h-4 mr-2 inline"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Reports
          </Button>
        </div>
      </div>

      {/* Season selector */}
      <div className="mb-6">
        <select
          value={selectedSeason}
          onChange={handleSeasonChange}
          className="px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Select season"
        >
          {availableSeasons.map((season) => (
            <option key={season} value={season}>
              {season}
            </option>
          ))}
        </select>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <select
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by group"
        >
          <option value="">All Groups</option>
          {uniqueGroups.map((group) => (
            <option key={group} value={group}>
              {group}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by category"
        >
          <option value="">All Categories</option>
          {uniqueCategories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
        <select
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by grade"
        >
          <option value="">All Grades</option>
          {uniqueGrades.map((grade) => (
            <option key={grade} value={grade}>
              {grade}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name..."
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px]"
          aria-label="Search riders"
        />
      </div>

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            <strong>Total Riders:</strong> {riders.length}
          </span>
          <span>
            <strong>Showing:</strong> {filteredRiders.length}
          </span>
          <span>
            <strong>Race Events:</strong> {raceEvents.length}
          </span>
          <span data-testid="summary-registered-any">
            <strong>Registered for ≥1 race:</strong> {registeredAtLeastOne}
          </span>
          <span data-testid="summary-total-registrations">
            <strong>Total race registrations:</strong> {totalRegistrations}
          </span>
        </div>
        {raceEvents.length > 0 && (
          <div className="mt-3">
            <div className="text-sm font-semibold text-gray-700 mb-1">
              Registered riders per race:
            </div>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
              {raceEvents.map((e) => (
                <li key={e.event_id} data-testid={`race-count-${e.event_id}`}>
                  <span className="font-medium">{e.event_name}</span>
                  {' — '}
                  <span className="text-[#435d7d] font-semibold">
                    {perRaceCounts[e.event_id] ?? 0}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Empty state */}
      {filteredRiders.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No riders found for the {selectedSeason} season.
        </div>
      ) : raceEvents.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No race events found for the {selectedSeason} season.
        </div>
      ) : (
        /* Report table */
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                  Category
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                  Group
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                  Grade
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b">
                  # of Races
                </th>
                {raceEvents.map((event) => (
                  <th
                    key={event.event_id}
                    className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b min-w-[100px] max-w-[160px]"
                  >
                    {event.event_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRiders.map((rider) => (
                <tr
                  key={rider.rider_id}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-sm font-medium">{rider.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rider.category}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rider.group}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rider.grade}</td>
                  <td
                    className="px-4 py-3 text-center text-sm font-semibold text-gray-700"
                    data-testid={`rider-race-count-${rider.rider_id}`}
                  >
                    {countRacesForRider(rider, raceEvents)}
                  </td>
                  {raceEvents.map((event) => {
                    const registered = getRegistrationStatus(
                      rider.race_registrations,
                      event.event_id,
                    );
                    return (
                      <td
                        key={event.event_id}
                        className="px-4 py-3 text-center"
                      >
                        {registered ? (
                          <span
                            className="text-green-600 text-lg"
                            aria-label={`Registered for ${event.event_name}`}
                          >
                            ✓
                          </span>
                        ) : (
                          <span
                            className="text-red-600 text-lg"
                            aria-label={`Not registered for ${event.event_name}`}
                          >
                            ✗
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 font-semibold text-gray-800 border-t-2 border-gray-300">
                <td className="px-4 py-3 text-sm">TOTAL</td>
                <td className="px-4 py-3 text-sm" />
                <td className="px-4 py-3 text-sm" />
                <td className="px-4 py-3 text-sm" />
                <td
                  className="px-4 py-3 text-center text-sm"
                  data-testid="total-registrations"
                >
                  {totalRegistrations}
                </td>
                {raceEvents.map((event) => (
                  <td
                    key={event.event_id}
                    className="px-4 py-3 text-center text-sm text-[#435d7d]"
                    data-testid={`total-race-count-${event.event_id}`}
                  >
                    {perRaceCounts[event.event_id] ?? 0}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default RaceRegistrationReport;
